import { Component, HostListener, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CaptchaDto, InteractionDto } from '@prf/interaction';
import { QuerySubjectDto } from '@prf/query-subject';
import { TypeCustomerDto } from '@prf/type-customer';
import { TypeDocumentDto } from '@prf/type-document';
import { AceptacionesResultDto } from '@prf/ws-data-treatment';
import { Guid } from 'guid-typescript';
import { forkJoin, merge, Observable, Subject } from 'rxjs';
import { ModalLogic } from '../../../shared/logic/modal.logic';
import { EncryptService } from '../../../shared/services/encrypt/encrypt.service';
import { Data } from '../../Models/data.model';
import { FileData } from '../../Models/file-data.model';
import { Interaction } from '../../Models/interaction.model';
import { ModalsComponent } from '../modals/modals.component';

import { ContactUsFacade } from './contact-us.facade';
import { map, startWith } from 'rxjs/operators';
import emojiRegex from 'emoji-regex';
import { environment } from 'apps/website/src/environments/environment';

/**
 * Componente de Contacto
 */
@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss'],
})
export class ContactUsComponent implements OnInit {
  /**
   * Servicio encargado de interactuar con el servicio de encriptacion
   */
  private readonly encryptService: EncryptService;
  /**
   * Fachada Login
   */
  public readonly facade: ContactUsFacade;
  /**
   * Observable - Tipos de documentos
   */
  public readonly typeDocuments$: Observable<TypeDocumentDto[]> = new Observable();
  /**
   * Observable - Tipos de clientes
   */
  public readonly typeCustomers$: Observable<TypeCustomerDto[]> = new Observable();
  /**
   * Observable - Asustos de consulta
   */
  public readonly querySubject$: Observable<QuerySubjectDto[]> = new Observable();
  /**
   * Observable que indica el estado de habilitación/deshabilitación del botón de archivo.
   */
  public infoDisableButtonFileUp$: Observable<boolean> = new Observable();
  /**
   * Información de los archivos seleccionados
   */
  public dataSelected$ = new Observable<FileData[]>();
  /**
   * Formulario
   */
  public frmGroup: FormGroup;
  /**
   * Lógica de Modals
   */
  public modalLogic: ModalLogic = new ModalLogic();
  /**
   * Ancho de la pantalla
   */
  public screenWidth!: number;
  /**
   * Número de caracteres en la descripción
   */
  public valor: number;
  /**
   * Provee una ventana flotante
   */
  public dialog: MatDialog;
  /**
   * Servicio para registrar y mostrar iconos usados por el componente `<mat-icon>`.
   */
  private readonly matIconRegistry: MatIconRegistry;
  /**
   * DomSanitizer ayuda a prevenir errores de Cross Site Scripting Security (XSS) al desinfectar valores para ser seguros de usar en los diferentes contextos DOM.
   */
  private readonly domSanitizer: DomSanitizer;
  /**
   * Identifica si eps esta seleccionado
   */
  isEPSSelected: boolean = false;
  /**
   * Indica si se debe mostrar el campo de tipo de documento.
   */
  public showEpsItems: boolean = false;
  /**
   * Indica si se debe mostrar la sección de pensión.
   */
  public showPension: boolean = false;
  /**
   * Sujeto que emite cambios en el asunto de consulta.
   */
  private subjectChange = new Subject<any>();
  /**
   * Sujeto que emite cambios en el tipo de cliente.
   */
  private typeCustomerChange = new Subject<any>();
  /**
   * Flag si el archivo es requerido
   */
  public isFileRequired = false;
  /**
   * Flag que indica si el archivo esta cargado
   */
  public isFileAttached = false;

  @HostListener('window:resize', ['$event'])
  public getScreenSize() {
    this.screenWidth = window.innerWidth;
  }

  /**
   * Constructor del componente Contact-Us
   * @param facade Facade Contact-Us
   * @param fb Creates an AbstractControl from a user-specified configuration.
   * @param matIconRegistry Servicio para registrar y mostrar iconos usados por el componente `<mat-icon>`.
   * @param domSanitizer Ayuda a prevenir errores de Cross Site Scripting Security (XSS) al desinfectar valores para ser seguros de usar en los diferentes contextos DOM.
   */
  public constructor(
    facade: ContactUsFacade,
    fb: FormBuilder,
    matIconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
    dialog: MatDialog,
    encryptService: EncryptService,
  ) {
    this.valor = 0;
    this.facade = facade;
    this.typeDocuments$ = this.facade.typeDocuments$;
    this.typeCustomers$ = this.facade.typeCustomers$;
    this.querySubject$ = this.facade.querySubjects$;
    this.encryptService = encryptService;
    this.dataSelected$ = this.facade.dataSelected$;
    this.infoDisableButtonFileUp$ = this.facade.infoDisableButtonFileUp$;
    this.frmGroup = fb.group(
      {
        typeCustomer: ['', Validators.required],
        name: ['', [Validators.required, Validators.maxLength(128), Validators.pattern(/^[a-zA-ZÀ-ÿ ñÑ]{1,64}$/)]],
        email: [
          '',
          [
            Validators.required,
            Validators.maxLength(200),
            Validators.pattern(/^(?!.*\.\.)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/),
          ],
        ],
        emailConfirmation: [
          '',
          [
            Validators.required,
            Validators.maxLength(200),
            Validators.pattern(/^(?!.*\.\.)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/),
          ],
        ],
        typeDocument: ['', Validators.required],
        numberDocument: [
          '',
          [
            Validators.required,
            Validators.maxLength(10),
            Validators.minLength(5),
            Validators.pattern(/^(?!0+$)[0-9]{5,10}$/),
          ],
        ],
        country: ['', [Validators.required, Validators.maxLength(64), Validators.pattern(/^[a-zA-ZÀ-ÿ ñÑ]{1,64}$/)]],
        municipality: [
          '',
          [Validators.required, Validators.maxLength(64), Validators.pattern(/^[a-zA-ZÀ-ÿ ñÑ]{1,64}$/)],
        ],
        department: ['', [Validators.required, Validators.maxLength(64), Validators.pattern(/^[a-zA-ZÀ-ÿ ñÑ]{1,64}$/)]],
        subject: ['', Validators.required],
        typeDocumentAfi: [''],
        numberDocumentAfi: [''],
        nameAfi: [''],
        typeConcep: [''],
        detailSubject: ['', [Validators.required, Validators.maxLength(500), this.noEmojiValidator()]],
        phone: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(/^[0-9]{1,10}$/)]],
        address: ['', [Validators.required, Validators.maxLength(80), this.noEmojiValidator()]],
        dataTreatmentYes: [false, Validators.requiredTrue],
        dataTreatmentNo: false,
        acceptedTermsYes: [false, Validators.requiredTrue],
        acceptedTermsNo: false,
        recaptchaReactive: [null],
      },
      { validators: this.emailConfirmationValidator() },
    );

    this.frmGroup.get('subject')?.valueChanges.subscribe((value) => this.subjectChange.next(value));
    this.frmGroup.get('typeCustomer')?.valueChanges.subscribe((value) => this.typeCustomerChange.next(value));
    merge(
      this.subjectChange.pipe(startWith(this.frmGroup.get('subject')?.value)),
      this.typeCustomerChange.pipe(startWith(this.frmGroup.get('typeCustomer')?.value)),
    )
      .pipe(
        map(() => ({
          subject: this.frmGroup.get('subject')?.value,
          typeCustomer: this.frmGroup.get('typeCustomer')?.value,
        })),
      )
      .subscribe(({ subject, typeCustomer }) => {
        this.updateValidatorsForTypeDocumentAfi(subject, typeCustomer);
      });

    this.matIconRegistry = matIconRegistry;
    this.domSanitizer = domSanitizer;

    this.frmGroup.get('typeCustomer')?.valueChanges.subscribe((value) => {
      const isEpsSelected = value && value.name === 'EPS';
      this.isEPSSelected = isEpsSelected;

      if (!isEpsSelected) {
        this.frmGroup.get('subject')?.setValue('');
        this.frmGroup.get('typeDocumentAfi')?.setValue('');
        this.frmGroup.get('numberDocumentAfi')?.setValue('');
        this.frmGroup.get('nameAfi')?.setValue('');
        this.frmGroup.get('typeConcep')?.setValue('');
      }
    });

    this.matIconRegistry.addSvgIcon(
      `choose_file`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/images/Gráficos_Mesa de trabajo1.svg'),
    );

    this.dialog = dialog;
    this.getScreenSize();
  }

  /**
   * Método encargado de inicializar el componente
   */
  public ngOnInit(): void {
    this.facade.allTypeDocuments(() => {
      this.facade.allTypeCustomers(() => {
        this.facade.allQuerySubjects();
      });
    });
    this.frmGroup.get('subject')?.valueChanges.subscribe(() => this.evaluateFields());
    this.frmGroup.get('typeCustomer')?.valueChanges.subscribe(() => this.evaluateFields());
  }

  /**
   * Evalúa los campos del formulario para determinar si la carga de archivos debe ser obligatoria.
   * Este método se ejecuta cuando cambian los valores de 'subject' o 'typeCustomer' en el formulario.
   *
   * Lógica de negocio: Los archivos son obligatorios únicamente cuando se selecciona
   * la combinación específica de "Concepto rehabilitación/EPS" como asunto y "EPS" como tipo de cliente.
   */
  public evaluateFields(): void {
    // Obtener los valores actuales de los campos del formulario
    const subject = this.frmGroup.get('subject')?.value;
    const typeCustomer = this.frmGroup.get('typeCustomer')?.value;

    // Extraer el nombre del asunto, manejando tanto strings como objetos
    // Si es string, usar directamente; si es objeto, usar la propiedad 'name'
    const subjectName = typeof subject === 'string' ? subject : subject?.name;

    // Extraer el nombre del tipo de cliente, manejando tanto strings como objetos
    // Si es string, usar directamente; si es objeto, usar la propiedad 'name'
    const typeCustomerName = typeof typeCustomer === 'string' ? typeCustomer : typeCustomer?.name;

    // Verificar si se cumple la condición específica para requerir archivos
    if (subjectName === 'Concepto rehabilitación/EPS' && typeCustomerName === 'EPS') {
      // Establecer que los archivos son obligatorios para esta combinación específica
      this.isFileRequired = true;
    } else {
      // Para cualquier otra combinación, los archivos son opcionales
      this.isFileRequired = false;
    }
  }

  /**
   * Maneja el evento emitido por el componente hijo de carga de archivos.
   * Este método se ejecuta cuando cambia el estado de los archivos adjuntos.
   * @param attached - true si hay archivos adjuntos, false si no hay archivos o si hubo errores
   */
  public onFileStatusChanged(attached: boolean): void {
    // Actualizar el estado interno que indica si hay archivos adjuntos
    this.isFileAttached = attached;
  }

  /**
   * Método para verificar si el botón de envío debe estar habilitado
   * @returns true si el botón debe estar habilitado, false si debe estar deshabilitado
   */
  public isSubmitButtonEnabled(): boolean {
    // Si el archivo es requerido, debe estar adjunto para habilitar el botón
    if (this.isFileRequired) {
      return this.isFileAttached && this.frmGroup.valid;
    }
    // Si el archivo no es requerido, solo verifica que el formulario sea válido
    return this.frmGroup.valid;
  }

  /**
   * Método para verificar si el formulario puede ser enviado
   * @returns true si el formulario puede ser enviado
   */
  public canSubmitForm(): boolean {
    return this.isSubmitButtonEnabled();
  }

  /**
   * Actualiza los validadores para los campos de tipo de documento afiliado basado en los valores seleccionados.
   * @param subjectValue - El valor actual del campo 'Asunto de consulta'.
   * @param typeCustomerValue - El valor actual del campo 'Tipo de cliente'.
   */
  private updateValidatorsForTypeDocumentAfi(subjectValue: any, typeCustomerValue: any): void {
    // Obtiene el control del formulario de los datos especificados
    const typeDocumentControl = this.frmGroup.get('typeDocumentAfi');
    const numberDocumentControl = this.frmGroup.get('numberDocumentAfi');
    const nameAndLastNameControl = this.frmGroup.get('nameAfi');
    const typeConcepControl = this.frmGroup.get('typeConcep');

    // Determina si los campos están seleccionados basándose en los valores proporcionados.
    const isEPSSelected = subjectValue?.name === 'Concepto rehabilitación/EPS';
    const isTypeEpsSelected = typeCustomerValue?.name === 'EPS';

    // Configura los validadores basándose en las condiciones.
    if (isEPSSelected && isTypeEpsSelected) {
      // Establece los validadores para el tipo de documento afiliado como requerido.
      typeDocumentControl?.setValidators([Validators.required]);

      // Establece los validadores para el número de documento afiliado.
      numberDocumentControl?.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(5),
        Validators.pattern(/^[0-9]{5,10}$/),
      ]);

      // Establece los validadores para el nombre y apellido del afiliado.
      nameAndLastNameControl?.setValidators([
        Validators.required,
        Validators.maxLength(128),
        Validators.pattern(/^[a-zA-ZÀ-ÿ ñÑ]{1,64}$/),
      ]);

      // Establece los validadores para el tipo de concepto como requerido.
      typeConcepControl?.setValidators([Validators.required]);

      // Muestra los campos para Eps
      this.showEpsItems = true;
    } else {
      // Si ninguna de las condiciones anteriores se cumple, limpia los validadores.
      typeDocumentControl?.clearValidators();
      numberDocumentControl?.clearValidators();
      nameAndLastNameControl?.clearValidators();
      typeConcepControl?.clearValidators();

      // Oculta los campos correspondientes.
      this.showEpsItems = false;
      this.showPension = false;
    }

    // Actualiza la validez de los controles después de modificar los validadores.
    typeDocumentControl?.updateValueAndValidity();
    numberDocumentControl?.updateValueAndValidity();
    nameAndLastNameControl?.updateValueAndValidity();
    typeConcepControl?.updateValueAndValidity();
  }

  /**
   * Metodo para el control del checkbox de tratamiento de datos
   */
  public toggleCheckboxData(checkboxName: string) {
    if (checkboxName === 'dataTreatmentYes') {
      const habeasControl = this.frmGroup.get('dataTreatmentYes');
      const habeas2Control = this.frmGroup.get('dataTreatmentNo');

      if (habeasControl?.value) {
        habeas2Control?.setValue(false);
      }
    } else if (checkboxName === 'dataTreatmentNo') {
      const habeasControl = this.frmGroup.get('dataTreatmentYes');
      const habeas2Control = this.frmGroup.get('dataTreatmentNo');

      if (habeas2Control?.value) {
        habeasControl?.setValue(false);
      }
    }
  }
  /**
   * Metodo para el control del checkbox de terminos y condiciones
   */
  public toggleCheckboxTerms(checkboxName: string) {
    if (checkboxName === 'acceptedTermsYes') {
      const habeas3Control = this.frmGroup.get('acceptedTermsYes');
      const habeas4Control = this.frmGroup.get('acceptedTermsNo');

      if (habeas3Control?.value) {
        habeas4Control?.setValue(false);
      }
    } else if (checkboxName === 'acceptedTermsNo') {
      const habeas3Control = this.frmGroup.get('acceptedTermsYes');
      const habeas4Control = this.frmGroup.get('acceptedTermsNo');

      if (habeas4Control?.value) {
        habeas3Control?.setValue(false);
      }
    }
  }
  /**
   * Método que obtiene tratamiento de datos
   */
  public async getTerminosyCondiciones() {
    this.facade.spinner.show();
    let datos = '';
    (await this.facade.getTratamientoDatos()).subscribe(async (x) => {
      const dataEncrypted = x.data?.toString() ?? '';
      const dataDecrypted = await this.encryptService.toDecrypt(dataEncrypted);
      const dataJson = JSON.parse(dataDecrypted);
      datos = (dataJson.listaDocumentos?.shift()?.contenidoDocumento || '') + ',Tratamiento de datos';
      this.facade.spinner.hide();
      this.dialog.open(ModalsComponent, {
        width: '850px',
        height: '600px',
        closeOnNavigation: true,
        data: datos,
      });
    });
  }
  /**
   * Método que obtiene términos y condiciones
   */
  public async getTerminosyCondiciones2() {
    this.facade.spinner.show();
    let datos = '';
    (await this.facade.getTermsAndConditions()).subscribe(async (x) => {
      const dataEncrypted = x.data?.toString() ?? '';
      const dataDecrypted = await this.encryptService.toDecrypt(dataEncrypted);
      const dataJson = JSON.parse(dataDecrypted);
      datos = (dataJson.listaDocumentos?.shift()?.contenidoDocumento || '') + ',Términos y condiciones';
      this.facade.spinner.hide();
      this.dialog.open(ModalsComponent, {
        width: '850px',
        height: '600px',
        closeOnNavigation: true,
        data: datos,
      });
    });
  }

  /**
   * Método de guardado
   */
  public async onSubmit() {
    // Validar el botón para evitar envíos duplicados
    if (this.validateButton()) {
      return;
    }

    // Obtener los datos del formulario
    const form = this.frmGroup.value;

    // Cargar los adjuntos al file server
    const uploadFiles = await this.SendFileServer();
    let countAttachments = 0;
    let attachments = false;

    if (uploadFiles !== '') {
      // Parsear la respuesta para convertirla en un array de objetos
      const filesArray = JSON.parse(uploadFiles);

      // Determinar si hay adjuntos
      attachments = filesArray.length > 0;

      // Contar la cantidad de adjuntos
      countAttachments = filesArray.length + (form.nameAfi ? 1 : 0);
    }

    // Crear un modelo de datos con la información del formulario
    const dataModel: Data = {
      customerType: form.typeCustomer.name,
      typeDocument: form.typeDocument.mask,
      querySubject: form.subject.name,
      numberDocument: form.numberDocument,
      name: form.name,
      email: form.email,
      phone: form.phone,
      country: form.country,
      department: form.department,
      municipality: form.municipality,
      address: form.address,
      queryDetail: form.detailSubject,
      attachments: attachments,
      countAttachments: countAttachments,
      dataTreatment: form.dataTreatmentYes,
      acceptedTerms: form.acceptedTermsYes,
      typeDocumentAffiliate: `${form.typeDocumentAfi?.mask ?? ''}`,
      numberDocumentAffiliate: `${form.numberDocumentAfi ?? ''}`,
      nameAffiliate: `${form.nameAfi ?? ''}`,
      concept: `${form.typeConcep ?? ''}`,
      attachmentsInfo: uploadFiles,
    };

    // Crear un modelo de interacción para el registro de la interacción
    const interactionModel: Interaction = {
      idInteraction: 1,
      // Generar un GUID para la interacción
      guId: Guid.create().toString(),
      // Datos del modelo de datos creado anteriormente
      data: dataModel,
      date: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString(),
    };

    // Crear un DTO de interacción para enviar al servicio de autenticación
    const interactionDto: InteractionDto = {
      typeDocument: form.typeDocument.mask,
      numberDocument: form.numberDocument,
      // Convertir el modelo de interacción a JSON y limpiar espacios en blanco
      payload: `${JSON.stringify(interactionModel)}`.trim(),
      version: '1',
      idSequence: 2,
    };

    // Servicio que genera el token de Google Enterprise
    grecaptcha.enterprise.ready(async () => {
      const token = await grecaptcha.enterprise.execute(environment.googleCaptcha, { action: 'LOGIN' });

      // Crear un DTO de Captcha para enviar al servicio de autenticación
      const captcha: CaptchaDto = {
        googletoken: token,
        interactionData: interactionDto,
      };

      // Llamar al método de autenticación con los DTOs y modelos preparados
      this.auth(captcha, dataModel);
    });
  }

  /**
   * Metodo encargado de enviar los adjuntos al file server
   * @returns respuesta del file server
   */
  public async SendFileServer(): Promise<string> {
    let adjuntos: FileData[] = [];
    // Suscripción para obtener la lista de archivos seleccionados
    this.dataSelected$.subscribe((response) => (adjuntos = response));
    if (adjuntos.length > 0) {
      return this.facade.saveFileStorage(adjuntos);
    }
    return '';
  }

  /**
   * Metodo encargado de validar captcha y guardar la interaccion
   * @param captcha Informacion necesaria para validar el captcha y guardar la interaccion
   * @param dataModel Informacion del formulario
   */
  public auth(captcha: CaptchaDto, dataModel: Data) {
    this.facade.auth(captcha, (response) => {
      if (response) {
        // Obtener la aceptación de términos y condiciones y tratamiento de datos
        const termsAndConditionsObservable = this.facade.acceptanceTemsAndConditions(
          dataModel?.typeDocument || '',
          dataModel?.numberDocument || '',
        );
        const aceptacionObservable = this.facade.aceptacionTratamientoDatos(
          dataModel?.typeDocument || '',
          dataModel?.numberDocument || '',
        );

        // Combinar las respuestas de aceptación y términos y condiciones
        forkJoin([aceptacionObservable, termsAndConditionsObservable]).subscribe(
          async ([aceptacionResponse, termsAndConditionsResponse]) => {
            // Desencriptar y deserializar las respuestas de aceptación y términos y condiciones
            const aceptacionDecrypt = await this.encryptService.toDecrypt(aceptacionResponse.data || '');

            const aceptacionDeserealized = JSON.parse(aceptacionDecrypt) as AceptacionesResultDto;

            const termsAndConditionsDecrypt = await this.encryptService.toDecrypt(
              termsAndConditionsResponse.data || '',
            );

            const termsAndConditionsDeserealized = JSON.parse(termsAndConditionsDecrypt) as AceptacionesResultDto;

            // Verificar que las respuestas de aceptación y términos y condiciones sean exitosas
            if (
              aceptacionDeserealized.status?.statusCode === 200 &&
              termsAndConditionsDeserealized.status?.statusCode === 200
            ) {
              // Mostrar el modal de solicitud
              this.facade.spinner.hide();
              this.modalLogic.getRequestModal();
            } else {
              // Ocultar el spinner si la autenticación o aceptación de términos y condiciones falla
              this.facade.spinner.hide();
            }
          },
        );
      }
    });
  }

  /**
   * Método encargado de mostrar los modal según a quién hace la solitud
   */
  public modalTypeCustomer() {
    const form = this.frmGroup.value;

    switch (form.typeCustomer.id) {
      case 7:
        this.modalLogic.getEpsModal();
        break;
      case 8:
        this.modalLogic.getArlModal();
        break;
      case 9:
        this.modalLogic.getJudicialEntityModal();
        break;
      case 10:
        this.modalLogic.getQualifyModal();
        break;
      default:
    }
  }
  /**
   * Método encargado de mostrar los modal según a asunto de tu consulta
   */
  public modalQuerySubject() {
    const form = this.frmGroup.value;
    switch (form.subject.id) {
      case 5:
        this.modalLogic.getEpsRehabilitationModal();
        break;
      default:
    }
  }
  /**
   * Método encargado de contar los caracteres de la descripción
   */
  public count() {
    const form = this.frmGroup.value;
    this.valor = form.detailSubject.length;
  }

  /**
   * Valida el estado del botón de subida de archivos.
   *
   * @returns `true` si el botón debe estar deshabilitado, `false` en caso contrario.
   */
  public validateButton(): boolean {
    let response = false;
    this.infoDisableButtonFileUp$.subscribe((x) => {
      response = x;
    });

    return response;
  }

  /**
   * Metodo encargado de detectar emogis
   * @returns si el campo contiene emogis o no
   */
  public noEmojiValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Si el campo está vacío, no valida emojis
      }
      const regex = emojiRegex();
      const hasEmoji = regex.test(control.value);
      return hasEmoji ? { noEmoji: true } : null;
    };
  }

  /**
   * Metodo encargado de comparar el correo de confirmación
   * con el correo electrónico principal
   * @returns Función validadora
   */
  public emailConfirmationValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      // Obtiene el valor del campo email
      const email = group.get('email')?.value;

      // Obtiene el valor del campo confirmación de email
      const emailConfirmation = group.get('emailConfirmation')?.value;

      // Verifica que ningún campo esté vacío
      if (!email || !emailConfirmation) {
        return null;
      }

      // Define la condición
      const emailsMatch = email === emailConfirmation;

      // Retorna el objeto validador
      return emailsMatch ? null : { noMatchEmails: true };
    };
  }

  /**
   * Metodo encargado de prevenir los eventos del
   * portapapeles en el formulario
   */
  public preventDefault(event: ClipboardEvent): void {
    event.preventDefault();
  }
}
